import React, { useEffect, useMemo, useState } from 'react';
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Download,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import AppBar from 'components/AppBar';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Skeleton } from 'components/ui/skeleton';
import { getAllSuppliers, createSupplier, updateSupplier } from '../services/suppliersService';
import { getPurchaseOrdersSummaryForStats } from '../services/purchaseOrdersService';
import './Suppliers.css';

const ITEMS_PER_PAGE = 10;

const CATEGORY_OPTIONS = [
  'Cables',
  'Wooden poles',
  'Concrete Poles',
  'Transformer',
  'Fittings',
  'Car rental',
  'Local Transportation',
  'Crane',
  'Printing',
  'Dash camera',
  'Fuel',
];

const emptyForm = {
  name: '',
  contact: '',
  phone: '',
  email: '',
  address: '',
  category: '',
};

const SAMPLE_SUPPLIERS = [
  {
    id: 4,
    name: 'Multi cable',
    contact: 'Emily Davis',
    phone: '+255 444 789 012',
    email: 'info@multicable.com',
    address: 'Dodoma, Tanzania',
    category: 'Cables',
    numberOfPO: 31,
    totalPOAmount: 18900000,
  },
  {
    id: 10,
    name: 'Poles T',
    contact: 'Patricia White',
    phone: '+255 777 666 555',
    email: 'contact@polest.com',
    address: 'Dar es Salaam, Tanzania',
    category: 'Wooden poles',
    numberOfPO: 27,
    totalPOAmount: 16800000,
  },
  {
    id: 7,
    name: 'Satguru',
    contact: 'Michael Taylor',
    phone: '+255 111 234 567',
    email: 'info@satguru.com',
    address: 'Dar es Salaam, Tanzania',
    category: 'Car rental',
    numberOfPO: 22,
    totalPOAmount: 15200000,
  },
  {
    id: 1,
    name: 'Master one',
    contact: 'John Smith',
    phone: '+255 123 456 789',
    email: 'contact@masterone.com',
    address: 'Dar es Salaam, Tanzania',
    category: 'Local Transportation',
    numberOfPO: 24,
    totalPOAmount: 12500000,
  },
  {
    id: 6,
    name: 'Paff',
    contact: 'Robert Brown',
    phone: '+255 222 345 678',
    email: 'info@paff.com',
    address: 'Dar es Salaam, Tanzania',
    category: 'Fittings',
    numberOfPO: 18,
    totalPOAmount: 11200000,
  },
  {
    id: 9,
    name: 'Mufindi',
    contact: 'James Miller',
    phone: '+255 888 777 666',
    email: 'info@mufindi.com',
    address: 'Iringa, Tanzania',
    category: 'Wooden poles',
    numberOfPO: 19,
    totalPOAmount: 10500000,
  },
  {
    id: 11,
    name: 'Smiles',
    contact: 'Jennifer Martinez',
    phone: '+255 666 555 444',
    email: 'info@smiles.com',
    address: 'Dar es Salaam, Tanzania',
    category: 'Car rental',
    numberOfPO: 16,
    totalPOAmount: 9500000,
  },
  {
    id: 5,
    name: 'Metsec',
    contact: 'David Wilson',
    phone: '+255 333 456 789',
    email: 'contact@metsec.com',
    address: 'Tanga, Tanzania',
    category: 'Cables',
    numberOfPO: 15,
    totalPOAmount: 9200000,
  },
  {
    id: 2,
    name: 'Honle',
    contact: 'Sarah Johnson',
    phone: '+255 987 654 321',
    email: 'info@honle.com',
    address: 'Arusha, Tanzania',
    category: 'Transformer',
    numberOfPO: 18,
    totalPOAmount: 8750000,
  },
  {
    id: 8,
    name: 'Sunbird',
    contact: 'Lisa Anderson',
    phone: '+255 999 888 777',
    email: 'contact@sunbird.com',
    address: 'Dar es Salaam, Tanzania',
    category: 'Wooden poles',
    numberOfPO: 14,
    totalPOAmount: 7800000,
  },
  {
    id: 3,
    name: 'Mushale',
    contact: 'Mike Chen',
    phone: '+255 555 123 456',
    email: 'contact@mushale.com',
    address: 'Mwanza, Tanzania',
    category: 'Fittings',
    numberOfPO: 12,
    totalPOAmount: 6500000,
  },
];

function formatMoney(amount) {
  return `TZS ${Number(amount || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

function SuPageSkeleton() {
  return (
    <div className="su-skeleton" aria-busy="true" aria-label="Loading suppliers">
      <section className="su-intro" aria-hidden>
        <div className="su-intro-text">
          <Skeleton className="h-7 w-52 max-w-full" />
          <Skeleton className="mt-2.5 h-4 w-96 max-w-full" />
        </div>
        <div className="su-intro-actions">
          <Skeleton className="h-9 w-9 rounded-lg" />
          <Skeleton className="h-9 w-[8.5rem] rounded-lg" />
        </div>
      </section>
      <div className="su-summary-cards" aria-hidden>
        {[0, 1, 2].map((i) => (
          <div key={i} className="su-summary-card su-summary-card--skel">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <Skeleton className="mt-3 h-4 w-24" />
            <Skeleton className="mt-2 h-6 w-28" />
            <Skeleton className="mt-4 h-3 w-full" />
          </div>
        ))}
      </div>
      <div className="su-toolbar" aria-hidden>
        <Skeleton className="h-9 w-full max-w-md rounded-lg" />
        <Skeleton className="h-9 w-32 rounded-lg" />
      </div>
      <div className="su-table-shell" aria-hidden>
        <div className="su-skel-rows">
          {Array.from({ length: 6 }, (_, r) => (
            <Skeleton key={r} className="h-10 w-full rounded-md" />
          ))}
        </div>
      </div>
    </div>
  );
}

function SupplierFormFields({ form, onChange, idPrefix = '' }) {
  return (
    <>
      <div className="su-field-row">
        <div className="su-field">
          <Label htmlFor={`${idPrefix}name`}>Supplier name *</Label>
          <Input
            id={`${idPrefix}name`}
            name="name"
            value={form.name}
            onChange={onChange}
            required
            placeholder="Enter supplier name"
          />
        </div>
        <div className="su-field">
          <Label htmlFor={`${idPrefix}contact`}>Contact person *</Label>
          <Input
            id={`${idPrefix}contact`}
            name="contact"
            value={form.contact}
            onChange={onChange}
            required
            placeholder="Enter contact person"
          />
        </div>
      </div>

      <div className="su-field">
        <Label htmlFor={`${idPrefix}category`}>Category *</Label>
        <select
          id={`${idPrefix}category`}
          name="category"
          className="su-modal-select"
          value={form.category}
          onChange={onChange}
          required
        >
          <option value="">Select category</option>
          {CATEGORY_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>

      <div className="su-field-row">
        <div className="su-field">
          <Label htmlFor={`${idPrefix}phone`}>Phone</Label>
          <Input
            id={`${idPrefix}phone`}
            name="phone"
            type="tel"
            value={form.phone}
            onChange={onChange}
            placeholder="+255 XXX XXX XXX"
          />
        </div>
        <div className="su-field">
          <Label htmlFor={`${idPrefix}email`}>Email</Label>
          <Input
            id={`${idPrefix}email`}
            name="email"
            type="email"
            value={form.email}
            onChange={onChange}
            placeholder="email@example.com"
          />
        </div>
      </div>

      <div className="su-field">
        <Label htmlFor={`${idPrefix}address`}>Address</Label>
        <Input
          id={`${idPrefix}address`}
          name="address"
          value={form.address}
          onChange={onChange}
          placeholder="Enter address"
        />
      </div>
    </>
  );
}

function Suppliers() {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = ITEMS_PER_PAGE;
  const [poSummary, setPoSummary] = useState({
    totalCount: 0,
    totalAmount: 0,
    fetchedCount: 0,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = await getAllSuppliers();
        if (!cancelled) setSuppliers(data || []);
      } catch (err) {
        console.error('Error fetching suppliers:', err);
        if (!cancelled) {
          setError('Failed to load suppliers. Showing sample data.');
          setSuppliers(SAMPLE_SUPPLIERS);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await getPurchaseOrdersSummaryForStats();
        if (!cancelled) {
          setPoSummary({
            totalCount: s.totalCount,
            totalAmount: s.totalAmount,
            fetchedCount: s.fetchedCount,
            loading: false,
            error: null,
          });
        }
      } catch (err) {
        console.error('Error loading PO summary:', err);
        if (!cancelled) {
          setPoSummary((prev) => ({
            ...prev,
            loading: false,
            error: 'Could not load PO totals',
          }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const clearAllFilters = () => {
    setSearchQuery('');
    setCategoryFilter('all');
  };

  const hasActiveFilters = Boolean(searchQuery.trim()) || categoryFilter !== 'all';

  const categoriesInData = useMemo(() => {
    const set = new Set(
      suppliers.map((s) => String(s.category || '').trim()).filter(Boolean)
    );
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [suppliers]);

  const filteredSuppliers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return suppliers.filter((supplier) => {
      if (categoryFilter !== 'all' && supplier.category !== categoryFilter) return false;
      if (!query) return true;
      const searchableText = [
        supplier.name,
        supplier.contact,
        supplier.category,
        supplier.phone,
        supplier.email,
        supplier.address,
      ]
        .join(' ')
        .toLowerCase();
      return searchableText.includes(query);
    });
  }, [suppliers, searchQuery, categoryFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, categoryFilter]);

  const totalFiltered = filteredSuppliers.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const indexOfFirstItem = totalFiltered > 0 ? (safePage - 1) * itemsPerPage + 1 : 0;
  const indexOfLastItem = Math.min(safePage * itemsPerPage, totalFiltered);
  const paginatedSuppliers = useMemo(
    () => filteredSuppliers.slice((safePage - 1) * itemsPerPage, safePage * itemsPerPage),
    [filteredSuppliers, safePage, itemsPerPage]
  );

  const goToPage = (page) => {
    setCurrentPage(page);
    const tableWrapper = document.querySelector('.su-table-wrapper');
    if (tableWrapper) {
      tableWrapper.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const goToPreviousPage = () => {
    if (safePage > 1) goToPage(safePage - 1);
  };

  const goToNextPage = () => {
    if (safePage < totalPages) goToPage(safePage + 1);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else if (safePage <= 3) {
      for (let i = 1; i <= 4; i++) pageNumbers.push(i);
      pageNumbers.push('...');
      pageNumbers.push(totalPages);
    } else if (safePage >= totalPages - 2) {
      pageNumbers.push(1);
      pageNumbers.push('...');
      for (let i = totalPages - 3; i <= totalPages; i++) pageNumbers.push(i);
    } else {
      pageNumbers.push(1);
      pageNumbers.push('...');
      for (let i = safePage - 1; i <= safePage + 1; i++) pageNumbers.push(i);
      pageNumbers.push('...');
      pageNumbers.push(totalPages);
    }
    return pageNumbers;
  };

  const sumListedSupplierPoCounts = useMemo(
    () => suppliers.reduce((sum, s) => sum + (Number(s.numberOfPO) || 0), 0),
    [suppliers]
  );
  const sumListedSupplierPoAmounts = useMemo(
    () => suppliers.reduce((sum, s) => sum + (Number(s.totalPOAmount) || 0), 0),
    [suppliers]
  );

  const unmatchedPoCount = Math.max(0, poSummary.totalCount - sumListedSupplierPoCounts);

  const handleExportToExcel = () => {
    try {
      if (suppliers.length === 0) {
        alert('No data to export');
        return;
      }

      const exportData = suppliers.map((s) => ({
        'Supplier Name': s.name || '',
        'Contact Person': s.contact || '',
        Category: s.category || '',
        Phone: s.phone || '',
        Email: s.email || '',
        Location: s.address || '',
        'Number of PO': s.numberOfPO ?? 0,
        'Total PO Amount': s.totalPOAmount ?? 0,
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Suppliers');
      ws['!cols'] = [
        { wch: 22 },
        { wch: 20 },
        { wch: 20 },
        { wch: 18 },
        { wch: 26 },
        { wch: 24 },
        { wch: 14 },
        { wch: 18 },
      ];

      XLSX.writeFile(wb, `Suppliers_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      alert('Failed to export data. Please try again.');
    }
  };

  const handleOpenAdd = () => {
    setEditingSupplier(null);
    setForm({ ...emptyForm });
    setFormError('');
    setShowModal(true);
  };

  const handleOpenEdit = (supplier) => {
    setEditingSupplier(supplier);
    setForm({
      name: supplier.name || '',
      contact: supplier.contact || '',
      phone: supplier.phone === '-' ? '' : supplier.phone || '',
      email: supplier.email === '-' ? '' : supplier.email || '',
      address: supplier.address === '-' ? '' : supplier.address || '',
      category: supplier.category || '',
    });
    setFormError('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSupplier(null);
    setForm({ ...emptyForm });
    setFormError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitSupplier = async (e) => {
    e.preventDefault();
    if (!form.name || !form.contact || !form.category) return;

    setSaving(true);
    setFormError('');
    try {
      const payload = {
        name: form.name,
        contact: form.contact,
        phone: form.phone || '-',
        email: form.email || '-',
        address: form.address || '-',
        category: form.category,
      };

      if (editingSupplier) {
        await updateSupplier(editingSupplier.id, payload);
      } else {
        await createSupplier(payload);
      }

      const data = await getAllSuppliers();
      setSuppliers(data || []);
      handleCloseModal();
    } catch (err) {
      console.error('Error saving supplier:', err);
      setFormError('Failed to save supplier. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const summaryCards = [
    {
      id: 'suppliers',
      label: 'Total suppliers',
      value: suppliers.length,
      accent: '#3b82f6',
      icon: Users,
      meta: 'Registered',
      display: suppliers.length.toLocaleString('en-US'),
    },
    {
      id: 'pos',
      label: 'Total POs',
      value: poSummary.totalCount,
      accent: '#10b981',
      icon: ClipboardList,
      meta: poSummary.loading ? 'Loading…' : poSummary.error || 'From purchase orders',
      display: poSummary.loading ? '—' : poSummary.totalCount.toLocaleString('en-US'),
    },
    {
      id: 'amount',
      label: 'Total PO amount',
      value: poSummary.totalAmount,
      accent: '#f59e0b',
      icon: Wallet,
      meta: poSummary.loading ? 'Loading…' : 'All POs',
      display: poSummary.loading ? '—' : formatMoney(poSummary.totalAmount),
    },
  ];

  return (
    <div className="su-page">
      <div className="su-scroll">
        <div className="su-panel">
          <AppBar
            variant="inset"
            breadcrumbs={[
              { label: 'Platform', hideOnMobile: true },
              { label: 'Sagemcom', to: '/sagemcom' },
              { label: 'Logistics', to: '/sagemcom/logistics', hideOnMobile: true },
              { label: 'Suppliers' },
            ]}
          />

          {loading && suppliers.length === 0 && !error ? (
            <SuPageSkeleton />
          ) : (
            <>
              <section className="su-intro" aria-label="Suppliers overview">
                <div className="su-intro-text">
                  <h1 className="su-intro-title">Suppliers</h1>
                  <p className="su-intro-subtitle">
                    Manage supplier contacts and track linked purchase order volumes
                  </p>
                </div>
                <div className="su-intro-actions">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="su-soft-btn h-9 w-9"
                    onClick={handleExportToExcel}
                    disabled={suppliers.length === 0}
                    title="Download Excel"
                    aria-label="Download Excel"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button type="button" className="su-create-btn" onClick={handleOpenAdd}>
                    <Plus className="h-4 w-4" />
                    Add supplier
                  </Button>
                </div>
              </section>

              {error ? (
                <div className="su-error" role="alert">
                  {error}
                </div>
              ) : null}

              <div className="su-summary-cards">
                {summaryCards.map((card) => {
                  const Icon = card.icon;
                  return (
                    <div
                      key={card.id}
                      className="su-summary-card"
                      style={{ '--cat-accent': card.accent }}
                    >
                      <div className="su-summary-card__top">
                        <span className="su-summary-card__icon" aria-hidden>
                          <Icon className="h-4 w-4" />
                        </span>
                        <span className="su-summary-card__label">{card.label}</span>
                      </div>
                      <div className="su-summary-card__count">{card.meta}</div>
                      <div className="su-summary-card__amount">{card.display}</div>
                      {card.id === 'pos' &&
                      !poSummary.loading &&
                      !poSummary.error &&
                      unmatchedPoCount > 0 ? (
                        <div className="su-summary-card__meta">
                          <div>
                            <span>Matched to list</span>
                            <strong>{sumListedSupplierPoCounts}</strong>
                          </div>
                        </div>
                      ) : null}
                      {card.id === 'amount' &&
                      !poSummary.loading &&
                      Math.abs(sumListedSupplierPoAmounts - poSummary.totalAmount) > 1 ? (
                        <div className="su-summary-card__meta">
                          <div>
                            <span>Listed subtotal</span>
                            <strong>{formatMoney(sumListedSupplierPoAmounts)}</strong>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              <div className="su-table-section__head">
                <h2>
                  Suppliers list
                  <span className="su-table-section__count">{totalFiltered}</span>
                </h2>
              </div>

              <div className="su-toolbar">
                <div className="su-search">
                  <Search className="su-search__icon h-4 w-4" aria-hidden />
                  <Input
                    className="su-search__input"
                    placeholder="Search name, contact, category, location…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label="Search suppliers"
                  />
                </div>
                <select
                  className="su-filter-select"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  aria-label="Category filter"
                >
                  <option value="all">All categories</option>
                  {categoriesInData.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
                <div className="su-toolbar__spacer" aria-hidden="true" />
                <Button
                  type="button"
                  variant="ghost"
                  className="su-soft-btn h-9 gap-1.5"
                  onClick={clearAllFilters}
                  disabled={!hasActiveFilters}
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Clear
                </Button>
              </div>

              <div className="su-table-section">
                <div className="su-table-shell">
                  <div className="su-table-wrapper">
                    <table className="su-table">
                      <thead>
                        <tr>
                          <th>Supplier</th>
                          <th>Contact</th>
                          <th>Category</th>
                          <th>Phone</th>
                          <th>Email</th>
                          <th>Location</th>
                          <th>POs</th>
                          <th>PO amount</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedSuppliers.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="su-empty-row">
                              {loading
                                ? 'Loading suppliers…'
                                : 'No suppliers found. Click “Add supplier” to create one.'}
                            </td>
                          </tr>
                        ) : (
                          paginatedSuppliers.map((supplier) => (
                            <tr key={supplier.id}>
                              <td>
                                <div className="su-name-cell">
                                  <span className="su-name-icon" aria-hidden>
                                    <Building2 className="h-3.5 w-3.5" />
                                  </span>
                                  <span className="su-supplier-name">{supplier.name}</span>
                                </div>
                              </td>
                              <td>{supplier.contact || '—'}</td>
                              <td>
                                <span className="su-category-badge">
                                  {supplier.category || '—'}
                                </span>
                              </td>
                              <td className="su-muted">{supplier.phone || '—'}</td>
                              <td className="su-muted">{supplier.email || '—'}</td>
                              <td className="su-muted">{supplier.address || '—'}</td>
                              <td className="su-qty">
                                {(Number(supplier.numberOfPO) || 0).toLocaleString('en-US')}
                              </td>
                              <td className="su-value">
                                {formatMoney(supplier.totalPOAmount)}
                              </td>
                              <td>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="su-soft-btn h-8 gap-1.5"
                                  onClick={() => handleOpenEdit(supplier)}
                                  disabled={saving}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                  Edit
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {totalFiltered > 0 ? (
                  <div className="su-pagination">
                    <div className="su-pagination__info">
                      Showing {indexOfFirstItem}–{indexOfLastItem} of {totalFiltered}
                    </div>
                    <div className="su-pagination__controls">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="su-soft-btn h-8 w-8"
                        onClick={goToPreviousPage}
                        disabled={safePage === 1}
                        aria-label="Previous page"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <div className="su-pagination__numbers">
                        {getPageNumbers().map((pageNum, index) =>
                          pageNum === '...' ? (
                            <span key={`ellipsis-${index}`} className="su-pagination__ellipsis">
                              …
                            </span>
                          ) : (
                            <button
                              key={pageNum}
                              type="button"
                              className={`su-pagination__num${safePage === pageNum ? ' is-active' : ''}`}
                              onClick={() => goToPage(pageNum)}
                            >
                              {pageNum}
                            </button>
                          )
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="su-soft-btn h-8 w-8"
                        onClick={goToNextPage}
                        disabled={safePage === totalPages}
                        aria-label="Next page"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>

      {showModal ? (
        <div className="su-modal-overlay" onClick={handleCloseModal} role="presentation">
          <div
            className="su-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="su-modal-title"
          >
            <div className="su-modal__header">
              <h2 id="su-modal-title">
                {editingSupplier ? 'Edit supplier' : 'Add supplier'}
              </h2>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="su-soft-btn h-8 w-8"
                onClick={handleCloseModal}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <form className="su-modal__form" onSubmit={handleSubmitSupplier}>
              {formError ? (
                <div className="su-modal__error" role="alert">
                  {formError}
                </div>
              ) : null}

              <SupplierFormFields
                form={form}
                onChange={handleInputChange}
                idPrefix={editingSupplier ? 'edit-' : 'add-'}
              />

              <div className="su-modal__actions">
                <Button
                  type="button"
                  variant="ghost"
                  className="su-soft-btn"
                  onClick={handleCloseModal}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving
                    ? editingSupplier
                      ? 'Updating…'
                      : 'Saving…'
                    : editingSupplier
                      ? 'Save changes'
                      : 'Add supplier'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default Suppliers;
